import React, { useState, useEffect } from 'react';
import { PackagePlus, ShoppingBag, GripVertical, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { SharedItem, SharedItemType } from '../../types/shared-items';

interface SortableItemProps {
  id: string;
  item: SharedItem;
  index: number;
  onRemove: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, item, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 rounded-lg border-2 border-[#E85C0D] bg-[#E85C0D]/10"
    >
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="w-5 h-5 text-white/40" />
        </div>
        <div className="flex items-center gap-3 flex-1">
          {item.icon && Icons[item.icon as keyof typeof Icons] && (
            React.createElement(Icons[item.icon as keyof typeof Icons], {
              className: "w-6 h-6 text-[#E85C0D]"
            })
          )}
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-medium">{item.title}</h4>
              <span className="text-xs text-white/40">Position: {index + 1}</span>
            </div>
            <p className="text-sm text-white/70 mt-1">{item.description}</p>
          </div>
        </div>
        <button
          onClick={onRemove}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

interface SharedItemsManagerProps {
  type: SharedItemType;
  selectedItems: string[];
  onItemsChange: (items: string[]) => void;
}

const SharedItemsManager: React.FC<SharedItemsManagerProps> = ({ type, selectedItems, onItemsChange }) => {
  const [items, setItems] = useState<SharedItem[]>([]);
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchItems();
  }, [type]);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('shared_items')
        .select('*')
        .eq('type', type)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = selectedItems.indexOf(active.id);
      const newIndex = selectedItems.indexOf(over.id);
      onItemsChange(arrayMove(selectedItems, oldIndex, newIndex));
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3 mb-4">
          {type === 'include' ? (
            <PackagePlus className="w-6 h-6 text-[#E85C0D]" />
          ) : (
            <ShoppingBag className="w-6 h-6 text-[#E85C0D]" />
          )}
          <h3 className="text-lg font-semibold">
            {type === 'include' ? 'What\'s Included' : 'What to Bring'}
          </h3>
        </div>
      </div>

      <div className="space-y-8">
        <div className="space-y-2">
          {selectedItems.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={selectedItems}
                strategy={verticalListSortingStrategy}
              >
                {selectedItems.map((itemId, index) => {
                  const item = items.find(i => i.id === itemId);
                  if (!item) return null;

                  return (
                    <SortableItem
                      key={item.id}
                      id={item.id}
                      item={item}
                      index={index}
                      onRemove={() => onItemsChange(selectedItems.filter(id => id !== item.id))}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-center p-4 border-2 border-dashed border-white/10 rounded-lg">
              <p className="text-white/40">No items selected</p>
            </div>
          )}
        </div>

        {/* Available Items */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
          {items.filter(item => !selectedItems.includes(item.id)).map((item) => (
            <div
              key={item.id}
              className="p-4 rounded-lg border-2 border-white/10 hover:border-white/20 transition-all cursor-pointer hover:shadow-lg hover:scale-[1.02]"
              onClick={() => onItemsChange([...selectedItems, item.id])}
            >
              <div className="flex items-center gap-3">
                {item.icon && Icons[item.icon as keyof typeof Icons] && (
                  React.createElement(Icons[item.icon as keyof typeof Icons], {
                    className: "w-6 h-6"
                  })
                )}
                <div>
                  <h4 className="font-medium">{item.title}</h4>
                  <p className="text-sm text-white/70 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SharedItemsManager;